/**
 * Client-side multiplayer connection manager.
 * Connects to the PartyKit server and translates between
 * the server protocol and the React game state.
 */

import PartySocket from 'partysocket';

const LOCAL_PARTY_HOST = 'localhost:1999';
const PARTY_HOST_ENV = process.env.REACT_APP_PARTYKIT_HOST || '';

function resolvePartyHost() {
  if (PARTY_HOST_ENV) return PARTY_HOST_ENV;
  if (process.env.NODE_ENV !== 'development') return '';
  const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
  // In local dev, allow LAN testing (phone/tablet) by targeting the same host as CRA.
  if (browserHost) return `${browserHost}:1999`;
  return LOCAL_PARTY_HOST;
}

export function generateRoomCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export function createConnection({
  roomId,
  playerName,
  onLobbyUpdate,
  onStateUpdate,
  onMartiniQuote,
  onChatMessage,
  onChatHistory,
  onChatReset,
  onError,
}) {
  const partyHost = resolvePartyHost();
  if (!partyHost) {
    onError?.(
      'Multiplayer is not configured. Set REACT_APP_PARTYKIT_HOST in Vercel and redeploy.'
    );
    const noop = () => {};
    return {
      socket: null,
      send: noop,
      startGame: noop,
      discard: noop,
      surgeEqualizeDiscard: noop,
      pickUp: noop,
      pickUpAndDiscard: noop,
      createPile: noop,
      breakPile: noop,
      returnPileCard: noop,
      reorder: noop,
      chat: noop,
      martiniQuote: noop,
      surge: noop,
      ready: noop,
      nextRound: noop,
      playAgain: noop,
      leaveRoom: noop,
      disconnect: noop,
    };
  }

  const socket = new PartySocket({
    host: partyHost,
    room: roomId.toUpperCase(),
    party: 'surge',
    query: { name: playerName },
  });
  let intentionalClose = false;

  socket.addEventListener('message', (event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }

    switch (data.type) {
      case 'lobby':
        onLobbyUpdate?.(data);
        break;
      case 'state':
        onStateUpdate?.(data);
        break;
      case 'error':
        onError?.(data.message);
        break;
      case 'chat':
        onChatMessage?.(data.message);
        break;
      case 'chat_history':
        onChatHistory?.(Array.isArray(data.messages) ? data.messages : []);
        break;
      case 'chat_reset':
        onChatReset?.();
        break;
      case 'martiniQuote':
        onMartiniQuote?.(data.message);
        break;
      default:
        break;
    }
  });

  socket.addEventListener('close', () => {
    if (!intentionalClose) {
      onError?.('Disconnected from server');
    }
  });

  const send = (msg) => {
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(msg));
    }
  };

  return {
    socket,
    send,
    startGame: (options = {}) => send({ type: 'start', ...options }),
    discard: (cardId) => send({ type: 'discard', cardId }),
    surgeEqualizeDiscard: (cardId) => send({ type: 'surgeEqualizeDiscard', cardId }),
    pickUp: () => send({ type: 'pickUp' }),
    pickUpAndDiscard: (cardId) => send({ type: 'pickUpAndDiscard', cardId }),
    createPile: (cardIds) => send({ type: 'createPile', cardIds }),
    breakPile: (pileIndex) => send({ type: 'breakPile', pileIndex }),
    returnPileCard: (pileIndex, cardId) => send({ type: 'returnPileCard', pileIndex, cardId }),
    reorder: (fromIndex, toIndex) => send({ type: 'reorder', fromIndex, toIndex }),
    chat: (text) => send({ type: 'chat', text }),
    martiniQuote: () => send({ type: 'martiniQuote' }),
    surge: () => send({ type: 'surge' }),
    ready: () => send({ type: 'ready' }),
    nextRound: () => send({ type: 'nextRound' }),
    playAgain: () => send({ type: 'playAgain' }),
    leaveRoom: () => send({ type: 'leaveRoom' }),
    disconnect: () => {
      intentionalClose = true;
      socket.close();
    },
  };
}
